/**
 * Redux slice (src/store/slices/exportSlice.js).
 *
 * Data flow (typical):
 * - UI dispatches actions/thunks from `src/store/actions/*`.
 * - `reducers` handles synchronous state updates.
 * - `extraReducers` reacts to thunk pending/fulfilled/rejected actions.
 */

import { createSlice } from "@reduxjs/toolkit";
import {
  exportStudentsToExcel,
  exportTeachersToExcel,
  exportBookingsToExcel,
  exportPackagesToExcel,
  exportPaymentsToExcel,
  exportCoursesToExcel,
  exportEnquiriesToExcel,
  exportAllDataToExcel
} from "../actions/exportActions";
import { toast } from "react-toastify";
import { t } from "../../utils/i18nTranslate";

/**
 * Slice initial state.
 *
 * Keep this shape stable because it is consumed across many selectors/components.
 */
const initialState = {
  loading: false,
  exportHistory: [],
  lastExport: null,
  error: null,
  errorStatus: null,
  exportStats: {
    totalExports: 0,
    studentsExports: 0,
    teachersExports: 0,
    bookingsExports: 0,
    packagesExports: 0,
    paymentsExports: 0,
    coursesExports: 0,
    enquiriesExports: 0,
    completeExports: 0
  }
};

/**
 * RTK slice definition: exportSlice.
 */
const exportSlice = createSlice({
  name: "exports",
  initialState,
  reducers: {
    /**
     * Reducer: clearExportHistory.
     */
    clearExportHistory: (state) => {
      state.exportHistory = [];
      state.exportStats = initialState.exportStats;
    },
    /**
     * Reducer: clearExportError.
     */
    clearExportError: (state) => {
      state.error = null;
      state.errorStatus = null;
    }
  },
  /**
   * Reducer: extraReducers.
   */
  extraReducers: (builder) => {
    // Students Export
    builder
      // extraReducers handler: exportStudentsToExcel.pending
      .addCase(exportStudentsToExcel.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.errorStatus = null;
      })
      // extraReducers handler: exportStudentsToExcel.fulfilled
      .addCase(exportStudentsToExcel.fulfilled, (state, action) => {
        state.loading = false;
        state.lastExport = action.payload;
        state.exportHistory.unshift({
          id: Date.now(),
          type: 'students',
          ...action.payload
        });
        state.exportStats.totalExports += 1;
        state.exportStats.studentsExports += 1;
        toast.success(t("store.export.studentsExported"), {
          position: "top-right",
          autoClose: 3000,
        });
      })
      // extraReducers handler: exportStudentsToExcel.rejected
      .addCase(exportStudentsToExcel.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || action.payload;
        state.errorStatus = Number(action.payload?.status) || null;
        toast.error(state.error || t("store.export.studentsExportFailed"), {
          position: "top-right",
          autoClose: 5000,
        });
      })

    // Teachers Export
    builder
      // extraReducers handler: exportTeachersToExcel.pending
      .addCase(exportTeachersToExcel.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.errorStatus = null;
      })
      // extraReducers handler: exportTeachersToExcel.fulfilled
      .addCase(exportTeachersToExcel.fulfilled, (state, action) => {
        state.loading = false;
        state.lastExport = action.payload;
        state.exportHistory.unshift({
          id: Date.now(),
          type: 'teachers',
          ...action.payload
        });
        state.exportStats.totalExports += 1;
        state.exportStats.teachersExports += 1;
        toast.success(t("store.export.teachersExported"), {
          position: "top-right",
          autoClose: 3000,
        });
      })
      // extraReducers handler: exportTeachersToExcel.rejected
      .addCase(exportTeachersToExcel.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || action.payload;
        state.errorStatus = Number(action.payload?.status) || null;
        toast.error(state.error || t("store.export.teachersExportFailed"), {
          position: "top-right",
          autoClose: 5000,
        });
      })

    // Bookings Export
    builder
      // extraReducers handler: exportBookingsToExcel.pending
      .addCase(exportBookingsToExcel.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.errorStatus = null;
      })
      // extraReducers handler: exportBookingsToExcel.fulfilled
      .addCase(exportBookingsToExcel.fulfilled, (state, action) => {
        state.loading = false;
        state.lastExport = action.payload;
        state.exportHistory.unshift({
          id: Date.now(),
          type: 'bookings',
          ...action.payload
        });
        state.exportStats.totalExports += 1;
        state.exportStats.bookingsExports += 1;
        toast.success(t("store.export.bookingsExported"), {
          position: "top-right",
          autoClose: 3000,
        });
      })
      // extraReducers handler: exportBookingsToExcel.rejected
      .addCase(exportBookingsToExcel.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || action.payload;
        state.errorStatus = Number(action.payload?.status) || null;
        toast.error(state.error || t("store.export.bookingsExportFailed"), {
          position: "top-right",
          autoClose: 5000,
        });
      })

    // Packages Export
    builder
      // extraReducers handler: exportPackagesToExcel.pending
      .addCase(exportPackagesToExcel.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.errorStatus = null;
      })
      // extraReducers handler: exportPackagesToExcel.fulfilled
      .addCase(exportPackagesToExcel.fulfilled, (state, action) => {
        state.loading = false;
        state.lastExport = action.payload;
        state.exportHistory.unshift({
          id: Date.now(),
          type: 'packages',
          ...action.payload
        });
        state.exportStats.totalExports += 1;
        state.exportStats.packagesExports += 1;
        toast.success(t("store.export.packagesExported"), {
          position: "top-right",
          autoClose: 3000,
        });
      })
      // extraReducers handler: exportPackagesToExcel.rejected
      .addCase(exportPackagesToExcel.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || action.payload;
        state.errorStatus = Number(action.payload?.status) || null;
        toast.error(state.error || t("store.export.packagesExportFailed"), {
          position: "top-right",
          autoClose: 5000,
        });
      })

    // Payments Export
    builder
      // extraReducers handler: exportPaymentsToExcel.pending
      .addCase(exportPaymentsToExcel.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.errorStatus = null;
      })
      // extraReducers handler: exportPaymentsToExcel.fulfilled
      .addCase(exportPaymentsToExcel.fulfilled, (state, action) => {
        state.loading = false;
        state.lastExport = action.payload;
        state.exportHistory.unshift({
          id: Date.now(),
          type: 'payments',
          ...action.payload
        });
        state.exportStats.totalExports += 1;
        state.exportStats.paymentsExports += 1;
        toast.success(t("store.export.paymentsExported"), {
          position: "top-right",
          autoClose: 3000,
        });
      })
      // extraReducers handler: exportPaymentsToExcel.rejected
      .addCase(exportPaymentsToExcel.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || action.payload;
        state.errorStatus = Number(action.payload?.status) || null;
        toast.error(state.error || t("store.export.paymentsExportFailed"), {
          position: "top-right",
          autoClose: 5000,
        });
      })

    // Courses Export
    builder
      // extraReducers handler: exportCoursesToExcel.pending
      .addCase(exportCoursesToExcel.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.errorStatus = null;
      })
      // extraReducers handler: exportCoursesToExcel.fulfilled
      .addCase(exportCoursesToExcel.fulfilled, (state, action) => {
        state.loading = false;
        state.lastExport = action.payload;
        state.exportHistory.unshift({
          id: Date.now(),
          type: 'courses',
          ...action.payload
        });
        state.exportStats.totalExports += 1;
        state.exportStats.coursesExports += 1;
        toast.success(t("store.export.coursesExported"), {
          position: "top-right",
          autoClose: 3000,
        });
      })
      // extraReducers handler: exportCoursesToExcel.rejected
      .addCase(exportCoursesToExcel.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || action.payload;
        state.errorStatus = Number(action.payload?.status) || null;
        toast.error(state.error || t("store.export.coursesExportFailed"), {
          position: "top-right",
          autoClose: 5000,
        });
      })

    // Enquiries Export
    builder
      // extraReducers handler: exportEnquiriesToExcel.pending
      .addCase(exportEnquiriesToExcel.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.errorStatus = null;
      })
      // extraReducers handler: exportEnquiriesToExcel.fulfilled
      .addCase(exportEnquiriesToExcel.fulfilled, (state, action) => {
        state.loading = false;
        state.lastExport = action.payload;
        state.exportHistory.unshift({
          id: Date.now(),
          type: 'enquiries',
          ...action.payload
        });
        state.exportStats.totalExports += 1;
        state.exportStats.enquiriesExports += 1;
        toast.success(t("store.export.enquiriesExported"), {
          position: "top-right",
          autoClose: 3000,
        });
      })
      // extraReducers handler: exportEnquiriesToExcel.rejected
      .addCase(exportEnquiriesToExcel.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || action.payload;
        state.errorStatus = Number(action.payload?.status) || null;
        toast.error(state.error || t("store.export.enquiriesExportFailed"), {
          position: "top-right",
          autoClose: 5000,
        });
      })

    // Complete Export (All Data)
    builder
      // extraReducers handler: exportAllDataToExcel.pending
      .addCase(exportAllDataToExcel.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.errorStatus = null;
      })
      // extraReducers handler: exportAllDataToExcel.fulfilled
      .addCase(exportAllDataToExcel.fulfilled, (state, action) => {
        state.loading = false;
        state.lastExport = action.payload;
        state.exportHistory.unshift({
          id: Date.now(),
          type: 'complete',
          ...action.payload
        });
        state.exportStats.totalExports += 1;
        state.exportStats.completeExports += 1;
        toast.success(t("store.export.completeExported"), {
          position: "top-right",
          autoClose: 3000,
        });
      })
      // extraReducers handler: exportAllDataToExcel.rejected
      .addCase(exportAllDataToExcel.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || action.payload;
        state.errorStatus = Number(action.payload?.status) || null;
        toast.error(state.error || t("store.export.completeExportFailed"), {
          position: "top-right",
          autoClose: 5000,
        });
      });
  },
});

export const { clearExportHistory, clearExportError } = exportSlice.actions;
export default exportSlice.reducer;
